import EmberService, { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { DSData, DSSetup, Service } from 'webapp-types';
import { BillingFeature, CompareType, ModelName, PopupEvent, Theme } from 'webapp/enums/enums';
import { copyObject } from 'webapp/utils/common';
import { getHTMLElFromSVG } from 'webapp/utils/data';

import type { DataAction } from 'webapp/components/block/content/data/popup';
import BlockContentDataPopupComponent from 'webapp/components/block/content/data/popup';
import BlockPopupPreviewComponent from 'webapp/components/block/popup-preview';
import BoardPopupPreviewComponent from 'webapp/components/board/popup-preview';
import GoalsInsightsDrawerPopupComponent from 'webapp/components/goals/insights/drawer-popup';
import MetricDetailsDrawerPopupComponent from 'webapp/components/metric-details/drawer-popup';

import type { Block, DataSelect } from 'webapp/models-custom';
import type DsDataOutputBase from 'webapp/models-custom/ds-data/output/base';
import type DsDataOutputCompare from 'webapp/models-custom/ds-data/output/compare';
import DsDataOutputOther from 'webapp/models-custom/ds-data/output/other';
import type { MetricSelectionData } from 'webapp/models-custom/ds-data/output/top';
import DsDataOutputTopLevel from 'webapp/models-custom/ds-data/output/top';
import type DsDataPointBase from 'webapp/models-custom/ds-data/point/base';
import DsDataPointOther from 'webapp/models-custom/ds-data/point/other';
import DsDataPointTopLevel from 'webapp/models-custom/ds-data/point/top';
import DsDataPointWithTs from 'webapp/models-custom/ds-data/point/with-ts';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type SampleRecord from 'webapp/models/sample';
import DChartComparePoint from 'webapp/d-chart/point/compare-point';
import type { AnomalyPointData } from 'webapp/d-chart/point/point-base';
import type { PointData } from 'webapp/d-chart/point/primary-point';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

// not extending from-store bcos we dont have model-custom for SampleRecord
export default class ModelManagerSamplesService extends EmberService {
	@service('store') declare storeService: Service.Store;
	@service('metric-raw-data') declare metricRawDataService: Service.MetricRawData;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('popup') declare popupService: Service.Popup;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('billing') declare billingService: Service.Billing;

	createDChartPointsFromOutput(output: DsDataOutputTopLevel) {
		const dataRoot = output.dataRoot;

		// all points, not just with values
		const points = dataRoot.granulationLabels.map((labelData, index) => {
			const dsDataPoint = output.getPointForIndex(index);
			const point = this.createDChartPointFromPoint(output, dsDataPoint);
			if (!dsDataPoint) {
				point.index = index;
				point.granulationLabel = labelData;
			}
			return point;
		});

		return points;
	}

	createDChartPointFromPoint(output: DsDataOutputBase, dsDataPoint: DsDataPointBase | null) {
		// works also without point itself

		const pointData: PointData = {
			title: output.shortTitle,
			titleLong: output.fullTitle,
			format: output.format,
			scale: output.scale,
			loading: output.isDataIncomplete,
			mediaLoading: output.isMediaLoading, // media creatives loading status
			dateRange: output.dateRange,
			isSourceDataset: output.isSourceDataset,
		};

		if (dsDataPoint) {
			pointData.value = dsDataPoint.value;
			pointData.valueStr = dsDataPoint.valueStr;
		}

		if (dsDataPoint instanceof DsDataPointTopLevel) {
			pointData.incomplete = dsDataPoint.incomplete;
		}

		if (output.dataRoot?.dataSelect?.isAnomaly) {
			// the extra of an anomaly data select (the same object, Object.assign with one argument does not copy)
			const anomalyData = ((dsDataPoint as DsDataPointTopLevel | null)?.extra ||
				{}) as AnomalyPointData;
			pointData.anomalyData = anomalyData;
		}

		if (output.dataRoot?.dataSelect?.firstCompare?.isGoal) {
			const goalComparePoint = (output as DsDataOutputTopLevel).compareOutputs?.find(
				(cp: DsDataOutputCompare) =>
					cp.points.find((p) => p.compareSettings?.type === CompareType.GOAL),
			)?.point;

			pointData.goalData = goalComparePoint?.extra;
		}

		if (dsDataPoint instanceof DsDataPointWithTs) {
			pointData.index = dsDataPoint.index;
			pointData.granulationLabel = dsDataPoint.granulationLabel;
		}

		if (output instanceof DsDataOutputTopLevel || output instanceof DsDataOutputOther) {
			pointData.metricSelectionData = output.metricSelectionData;
		}

		if (output instanceof DsDataOutputOther) {
			pointData.index = output.index;
			pointData.granulationLabel = output.granulationLabel;
		}

		const dataSelect = output.dataRoot.dataSelect;
		// isTemplate on Board or Report
		const isTemplate =
			dataSelect.block?.board?.templateId ||
			dataSelect.block?.reportSlideFragment?.report?.templateId;
		const { isDatawallApp, isMobileApp } = this.navigationService;
		const showPopup =
			!isTemplate &&
			!isDatawallApp &&
			!isMobileApp &&
			!this.currentUserService.isViewer &&
			!this.accountService.isDemoAccount &&
			this.getAvailableActions(dataSelect).length > 0;

		if (showPopup) {
			const that = this;
			pointData.onClick = function (e: PointerEvent) {
				return that.showPopover(e, output.dataRoot.dataSelect, {
					metricSelectionData: point.metricSelectionData,
					granulationLabel: point.granulationLabel,
				});
			};
		}

		const point = new DChartPrimaryPoint(pointData);

		if (dsDataPoint instanceof DsDataPointTopLevel || dsDataPoint instanceof DsDataPointOther) {
			// get compares, if any

			const comparePoints = dsDataPoint.comparePoints.map((outputPointCompare) => {
				let comparePoint: DChartComparePoint;

				if (outputPointCompare instanceof DsDataPointOther) {
					comparePoint = DChartComparePoint.createFromOutputPointOther(
						outputPointCompare,
						point,
					);
				} else {
					comparePoint = DChartComparePoint.createFromOutputPointCompare(
						outputPointCompare,
						point,
					);
				}

				if (showPopup) {
					const that = this;

					comparePoint.onClick = function (e: PointerEvent) {
						let metricSetupData: DSSetup.MetricSetupData | null,
							dimensionValues: DSData.DimensionsAndValuesMap | undefined;

						if (comparePoint.compareSettings.metricSetup) {
							const metricSetup = new MetricSetup(
								comparePoint.compareSettings.metricSetup,
							);

							metricSetupData = metricSetup.metricSetupData;
							if (metricSetup.isDimensionSelected) {
								const firstDimension = metricSetup.dimensions[0]!;
								const firstAttribute =
									metricSetup.firstAttributeNameFromBasicFilters;
								dimensionValues = { [firstDimension]: firstAttribute };
							}
						} else {
							metricSetupData = point.metricSelectionData?.metricSetupData || null;
							dimensionValues = point.metricSelectionData?.dimensionValues;
						}

						return that.showPopover(e, output.dataRoot.dataSelect, {
							metricSelectionData: {
								metricSetupData,
								dimensionValues,
								metricName: outputPointCompare.output.metricName,
							},
							granulationLabel: comparePoint.granulationLabel,
						});
					};
				}

				return comparePoint;
			});
			point.comparePoints = comparePoints;
		}

		return point;
	}

	// ------ FAKING DATA ------

	duplicateSampleForBlockCopy(originalBlock: Block, newBlock: Block) {
		const originalSampledata = originalBlock.sample?.sampledata;
		if (originalSampledata) {
			const originalDataSelects = originalBlock.dataSelects;
			const newDataSelects = newBlock.dataSelects;
			const sampleCopy = copyObject(originalSampledata);

			sampleCopy.dsData.forEach((dsData) => {
				const originalDs = originalDataSelects.find((ds) => ds.numericId === dsData.dsId);
				if (originalDs) {
					const newDs = newDataSelects.find((ds) => {
						return (
							ds.type === originalDs.type &&
							ds.order === originalDs.order &&
							ds.isPrimary === originalDs.isPrimary &&
							ds.dateRangeCM.isEqual(originalDs.dateRange) &&
							ds.metricSetup.isMatchingMetricSetup(originalDs.metricSetup)
						);
					});
					if (newDs) {
						dsData.dsId = newDs.numericId!;
					}
				}
			});

			this.storeService.pushPayload({
				sample: {
					id: newBlock.id!,
					sampledata: sampleCopy,
				},
			});
		}
	}

	// ------ FETCHING RECORDS & MODELS ------

	modelName = ModelName.SAMPLE;

	get allRecords(): SampleRecord[] {
		return this.storeService.peekAll(this.modelName).slice() as SampleRecord[];
	}

	@cached
	get savedRecords() {
		return this.allRecords;
	}

	findRecord(id: string): SampleRecord | null {
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		this.savedRecords; // both allRecords and savedRecords need to be added to autotracking
		return (this.storeService.peekRecord(this.modelName, id) || null) as SampleRecord | null;
	}

	getAvailableActions(
		dataSelect: DataSelect,
		metricSetupData?: DSSetup.MetricSetupData | null,
	): DataAction[] {
		// No actions available for forecasts or when metric is missing
		if (dataSelect.isOneOfForecasts || !dataSelect.metricSetup.metric) {
			return [];
		}

		const actions: DataAction[] = [];

		if (
			dataSelect.isSourceDataset &&
			!dataSelect.metricSetup.metric.isTypeMetricCalculation &&
			this.billingService.checkIfCan(BillingFeature.ADVANCED_DATA_SOURCES)
		) {
			actions.push({
				id: 'viewRawData',
				label: 'View raw data',
				icon: 'Table',
			});
		}

		const goalForClickedPoint = metricSetupData?.key
			? this.mmGoalsService.getGoalForGoalKeyLong(metricSetupData.key)
			: null;

		if (goalForClickedPoint) {
			const shouldShowGoalDetails =
				!this.navigationService.isActivatedRouteGoals &&
				!this.popupService.isOpen(GoalsInsightsDrawerPopupComponent);

			if (shouldShowGoalDetails) {
				actions.push({
					id: 'viewGoalDetails',
					label: 'View goal details',
					icon: 'Target',
				});
			}
		} else {
			const shouldHideMetricDetails =
				this.navigationService.isActivatedRouteGoals ||
				this.popupService.isOpen(MetricDetailsDrawerPopupComponent);

			if (!shouldHideMetricDetails) {
				actions.push({
					id: 'viewMetricInsights',
					label: 'View metric insights',
					icon: 'MetricDetail',
				});
			}
		}

		return actions;
	}

	showPopover(
		event: PointerEvent,
		dataSelect: DataSelect,
		point: {
			granulationLabel: DSData.GranulationLabel | null;
			metricSelectionData?: Partial<MetricSelectionData> | null;
		},
	) {
		const availableActions = this.getAvailableActions(
			dataSelect,
			point.metricSelectionData?.metricSetupData,
		);

		if (availableActions.length === 0) {
			return;
		}

		const svgElement = event.target as SVGElement | null;

		if (!svgElement) {
			return;
		}

		const htmlEl = getHTMLElFromSVG(svgElement);
		const className = 'popup-data-wrapper';
		htmlEl.classList.add(className);

		document.body.appendChild(htmlEl);

		const forceDarkTheme =
			this.navigationService.isDesigner ||
			this.popupService.isOpen(BoardPopupPreviewComponent) ||
			this.popupService.isOpen(BlockPopupPreviewComponent);

		const popup = this.popupService.createContext(BlockContentDataPopupComponent, htmlEl, {
			popupClass: 'popup-type-block-content-data-popup',
			theme: forceDarkTheme ? Theme.DARK : this.navigationService.theme.id,
		});

		const closePopup = () => {
			void popup.close();
		};

		popup.on(PopupEvent.CLOSED, this, () => {
			window.removeEventListener('resize', closePopup);
			window.removeEventListener('scroll', closePopup);
			const els = document.body.querySelectorAll(`.${className}`);
			els.forEach((el) => {
				document.body.removeChild(el);
			});
		});

		popup.open({ dataSelect, point, event, actions: availableActions });

		window.addEventListener('resize', closePopup);
		window.addEventListener('scroll', closePopup);

		return popup;
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/samples': ModelManagerSamplesService;
	}
}
